import { useEffect, useId, useState } from 'react';
import type { ReactElement } from 'react';

import { useBulkUpsertMortgageRulesMutation } from 'features/notification-config/services/businessRules.api';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';

import {
  buildLeadManagementRules,
  describeLeadManagementChange,
  type FinancingVertical,
  type MortgageRulesState,
  resolveLeadManagementSelection,
} from '../model/mortgageRules';
import MortgageConfirmDialog from './MortgageConfirmDialog';

interface Props {
  projectId: number;
  projectName: string;
  rules: MortgageRulesState;
}

const VERTICAL_OPTIONS: { value: FinancingVertical; label: string; description: string }[] = [
  {
    value: 'mortgage',
    label: 'Hipotecario',
    description: 'Simulación de crédito, políticas de las entidades, documentos y estimación de capacidad.',
  },
  {
    value: 'leasing',
    label: 'Leasing habitacional',
    description: 'Simulación de leasing, políticas de las entidades y estimación de capacidad y renta requerida.',
  },
];

/*
 * One project that answers with a single prompt and agent, no modes to move between: a master without
 * sub-projects, or one product of a financial entity. Its rules never live apart, so the whole set is written
 * at once and the vertical is a choice rather than two independent switches — picking one turns the other off
 * in the same request.
 */
const MortgageLeadManagementCard = ({ projectId, projectName, rules }: Props): ReactElement => {
  const [bulkUpsert, { isLoading: isSaving }] = useBulkUpsertMortgageRulesMutation();
  /* The products of a financial entity render one card each on the same page, so the ids are per instance. */
  const idPrefix = useId();
  const stored = resolveLeadManagementSelection(rules);

  const [enabled, setEnabled] = useState(stored.enabled);
  const [vertical, setVertical] = useState<FinancingVertical | null>(stored.vertical);
  const [isConfirming, setIsConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEnabled(stored.enabled);
    setVertical(stored.vertical);
  }, [stored.enabled, stored.vertical]);

  const draft = { enabled, vertical: enabled ? vertical : null };
  const isDirty = draft.enabled !== stored.enabled || draft.vertical !== stored.vertical;
  const canSave = isDirty && (!draft.enabled || draft.vertical !== null);

  const handleConfirm = async (): Promise<void> => {
    setError(null);

    try {
      await bulkUpsert({ entityIds: [projectId], rules: buildLeadManagementRules(draft) }).unwrap();
      setIsConfirming(false);
    } catch (saveError) {
      setIsConfirming(false);
      setError(extractErrorMessage(saveError, 'No se pudo guardar la configuración de financiamiento.'));
    }
  };

  return (
    <section className="flex flex-col space-y-4 rounded-md border border-border p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1 pr-2">
          <Label htmlFor={`${idPrefix}-financing-toggle`} className="text-sm font-medium">
            Financiamiento
          </Label>
          <p className="text-xs text-text-muted">
            Habilita las herramientas de financiamiento del agente. Atiende con un solo agente, así que las reglas se
            activan juntas.
          </p>
        </div>
        <Switch
          id={`${idPrefix}-financing-toggle`}
          checked={enabled}
          disabled={isSaving}
          onCheckedChange={setEnabled}
        />
      </div>

      {enabled && (
        <RadioGroup
          className="gap-2"
          value={vertical ?? ''}
          onValueChange={(value) => setVertical(value as FinancingVertical)}
        >
          {VERTICAL_OPTIONS.map((option) => (
            <Label
              key={option.value}
              htmlFor={`${idPrefix}-vertical-${option.value}`}
              className="flex items-start gap-3 rounded-md border border-border px-3 py-2.5 font-normal"
            >
              <RadioGroupItem id={`${idPrefix}-vertical-${option.value}`} value={option.value} disabled={isSaving} />
              <span className="space-y-1">
                <span className="block text-sm font-medium">{option.label}</span>
                <span className="block text-xs text-text-muted">{option.description}</span>
              </span>
            </Label>
          ))}
        </RadioGroup>
      )}

      <div className="mt-auto flex items-start justify-end gap-4 pt-4">
        <Button type="button" disabled={!canSave || isSaving} onClick={() => setIsConfirming(true)}>
          Guardar
        </Button>
      </div>

      {error && (
        <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">{error}</p>
      )}

      <MortgageConfirmDialog
        copy={isConfirming ? describeLeadManagementChange(draft, projectName) : null}
        isLoading={isSaving}
        onCancel={() => setIsConfirming(false)}
        onConfirm={() => {
          void handleConfirm();
        }}
      />
    </section>
  );
};

export default MortgageLeadManagementCard;
